// src/components/LocationPickerModal.js
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from '../../contexts/LocationContext';
import { formatLocationForFirestore } from '../../utils/geoUtils';
import { FiSearch, FiX, FiNavigation } from 'react-icons/fi';
import './LocationPickerModal.css';
import { motion, AnimatePresence } from 'motion/react';

const popularCities = [
  { city: 'Hyderabad', state: 'Telangana', village: 'Gachibowli', latitude: 17.4401, longitude: 78.3489, display: 'Gachibowli, Hyderabad' },
  { city: 'Bangalore', state: 'Karnataka', village: 'Indiranagar', latitude: 12.9784, longitude: 77.6408, display: 'Indiranagar, Bangalore' },
  { city: 'Mumbai', state: 'Maharashtra', village: 'Bandra West', latitude: 19.0596, longitude: 72.8295, display: 'Bandra West, Mumbai' },
  { city: 'Delhi', state: 'Delhi', village: 'Connaught Place', latitude: 28.6315, longitude: 77.2167, display: 'Connaught Place, Delhi' },
  { city: 'Pune', state: 'Maharashtra', village: 'Koregaon Park', latitude: 18.5362, longitude: 73.8946, display: 'Koregaon Park, Pune' }
];

const LocationPickerModal = ({ onClose }) => {
  const { updateSiteLocation, reverseGeocode } = useLocation(); // ✅ added reverseGeocode
  const [loading, setLoading] = useState(false);
  const [isVisible, setVisible] = useState(false); // for animation
  const autocompleteInput = useRef(null);

  useEffect(() => {
    setVisible(true); // trigger open animation
  }, []);

  const handleClose = () => {
    setVisible(false); // trigger close animationq
    setTimeout(() => {
      onClose();
    }, 200); // MUST match transition duration
  };

  useEffect(() => {
  if (!window.google) return;

  const timer = setTimeout(() => {
    if (!autocompleteInput.current) return;

    const autocomplete = new window.google.maps.places.Autocomplete(
      autocompleteInput.current,
      {
        componentRestrictions: { country: 'in' },
        fields: ['address_components', 'geometry', 'formatted_address'],
      }
    );

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (!place.geometry) return;

      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();

      const address = { city: '', state: '', neighbourhood: '', village: '', pincode: '' };

      place.address_components.forEach(c => {
        if (c.types.includes('locality')) address.city = c.long_name;
        if (c.types.includes('administrative_area_level_1')) address.state = c.long_name;
        if (c.types.includes('sublocality_level_1')) address.village = c.long_name;
        if (c.types.includes('postal_code')) address.pincode = c.long_name;
      });

      const finalLoc = formatLocationForFirestore(address, lat, lng);
      finalLoc.display = place.formatted_address;

      updateSiteLocation(finalLoc);
      handleClose();
    });

  }, 100); // 👈 small delay fixes timing

  return () => clearTimeout(timer);

}, [updateSiteLocation]);

  // ✅ POPULAR CITIES (WORKING VERSION)
  const handlePopularCityClick = async (city) => {
    const loc = formatLocationForFirestore(
      {
        city: city.city,
        state: city.state,
        village: city.village,
        neighbourhood: city.village
      },
      city.latitude,
      city.longitude
    );

    loc.display = city.display;

    await updateSiteLocation(loc);
    handleClose();
  };

  // ✅ REVERSE GEOCODE (PLUGGED FROM WORKING CODE)
  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation not supported");
      return;
    }

    setLoading(true);

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;

          const detectedLocation = await reverseGeocode(latitude, longitude);
          console.log("Detected:", detectedLocation);

          if (!detectedLocation) throw new Error("No location");

          await updateSiteLocation(detectedLocation);
          handleClose();
        } catch (error) {
          console.error(error);
          alert("Failed to detect location");
        } finally {
          setLoading(false);
        }
      },
      (error) => {
        console.error(error);
        alert("Enable location permission");
        setLoading(false);
      }
    );
  };

  return (
    <AnimatePresence>
      {isVisible && (<motion.div
        className="location-modal-overlay"
        onClick={handleClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="location-modal-content"
          onClick={e => e.stopPropagation()}
          initial={{ opacity: 0, y: -8, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -8, scale: 0.98 }}
          transition={{ duration: 0.22, ease: "easeOut" }}
        >
          <div className="modal-header">
            <h3>Select Location</h3>
            <button onClick={handleClose} className="close-btn"><FiX /></button>
          </div>

          <div className="modal-body">
            <div className="loc-search-wrapper">
              <FiSearch className="search-icon" />
              <input
                ref={autocompleteInput}
                type="text"
                placeholder="Search city, neighborhood..."
                autoFocus
              />
            </div>

            <button className="current-loc-btn" onClick={handleUseCurrentLocation}>
              <FiNavigation />
              <span>{loading ? 'Locating...' : 'Use current location'}</span>
            </button>

            <div className="popular-cities">
              <label>Popular Cities</label>
              <div className="city-tags">
                {popularCities.map(city => (
                  <span
                    key={city.city}
                    className="city-tag"
                    onClick={() => handlePopularCityClick(city)}
                  >
                    {city.city}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>)}
    </AnimatePresence>
  );
};

export default LocationPickerModal;